import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GHLNotConnected from "@/components/GHLNotConnected";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Receipt, Loader2 } from "lucide-react";

const statusColor = (s) => {
  const v = (s || "").toLowerCase();
  if (v === "paid") return "bg-emerald-500 text-white";
  if (v === "sent" || v === "due") return "bg-gold-500 text-white";
  return "bg-navy-50 text-navy-700";
};

export default function Invoices() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["ghl-invoices"],
    queryFn: async () => (await api.get("/ghl/invoices?limit=50")).data,
    retry: false,
  });

  const notConnected = error?.response?.status === 400;
  const invoices = data?.invoices || (Array.isArray(data) ? data : []);

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Invoices" subtitle="Payment records pulled securely from your GoHighLevel sub-account." />

      {isLoading ? (
        <div className="flex items-center gap-3 text-navy-500"><Loader2 className="h-5 w-5 animate-spin text-gold-500" /> Loading from GoHighLevel…</div>
      ) : notConnected ? (
        <GHLNotConnected icon={Receipt} title="Connect to view invoices" description="Link your GoHighLevel sub-account to display client invoices and payment status here." />
      ) : error ? (
        <Card className="rounded-2xl border-navy-900/5 bg-white p-6 text-navy-600" data-testid="ghl-error">Couldn't load invoices. {error.response?.data?.detail || ""}</Card>
      ) : invoices.length === 0 ? (
        <Card className="rounded-2xl border-navy-900/5 bg-white p-10 text-center text-navy-500">No invoices found.</Card>
      ) : (
        <Card className="overflow-hidden rounded-2xl border-navy-900/5 bg-white shadow-ambient">
          <Table>
            <TableHeader>
              <TableRow className="bg-ivory hover:bg-ivory">
                <TableHead className="text-navy-500">Invoice</TableHead>
                <TableHead className="text-navy-500">Contact</TableHead>
                <TableHead className="text-navy-500">Amount</TableHead>
                <TableHead className="text-navy-500">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((inv, i) => (
                <TableRow key={inv._id || inv.id || i} data-testid={`invoice-row-${i}`}>
                  <TableCell className="font-medium text-navy-900">{inv.invoiceNumber || inv.name || inv._id || `#${i + 1}`}</TableCell>
                  <TableCell className="text-navy-600">{inv.contactDetails?.name || inv.contactName || "—"}</TableCell>
                  <TableCell className="text-navy-900">{inv.currency || "$"} {inv.total ?? inv.amountDue ?? "—"}</TableCell>
                  <TableCell><Badge className={`border-0 ${statusColor(inv.status)}`}>{inv.status || "—"}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
